import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search, Car, Users as PassengersIcon, Briefcase } from 'lucide-react';

import { useApi } from '../../lib/api.js';
import { computeDisplayStatus } from '../../lib/computeStatus.js';
import { useCurrentUser } from '../../hooks/useCurrentUser.js';
import { PageHeader, LoadingPage, ErrorPage, EmptyState, StatusPill } from '../../components/ui/Feedback.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Select } from '../../components/ui/Form.jsx';
import { cn } from '../../lib/cn.js';


const CLASS_OPTIONS = [
    { value: 'all', label: 'All classes' },
    { value: 'business', label: 'Business' },
    { value: 'first', label: 'First' },
    { value: 'suv', label: 'SUV' },
    { value: 'xl', label: 'XL' },
];

export function VehiclesListPage() {
    const api = useApi();
    const navigate = useNavigate();
    const { isAdmin } = useCurrentUser();
    const [search, setSearch] = useState('');
    const [authorityFilter, setAuthorityFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('active');
    const [classFilter, setClassFilter] = useState('all');

    const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['vehicles', { status: statusFilter, search, authority: authorityFilter, vehicle_class: classFilter }],
    queryFn: () => api.get('/vehicles', {
        query: {
            status: statusFilter || undefined,
            search: search || undefined,
            authority: authorityFilter || undefined,
            vehicle_class: classFilter !== 'all' ? classFilter : undefined,
        }
    }),
});

    const vehicles = data?.vehicles || [];

    const actions = isAdmin ? (
        <Button onClick={() => navigate('/vehicles/new')}>
            <Plus className="h-4 w-4" />
            New vehicle
        </Button>
    ) : null;

    return (
        <div>
            <PageHeader
                title="Vehicles"
                subtitle="Manage fleet vehicles, documents, and chauffeur pairings."
                actions={actions}
            />

            <div className="card mb-5">
                <div className="card-body">
                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-subtle pointer-events-none" />
                            <input
                                type="search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by registration, make, model, nickname…"
                                className="input pl-9"
                            />
                        </div>
                        <Select
                            value={classFilter}
                            onChange={(e) => setClassFilter(e.target.value)}
                            className="sm:w-40"
                            aria-label="Filter by vehicle class"
                        >
                            {CLASS_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </Select>

                        <Select
    value={authorityFilter}
    onChange={(e) => setAuthorityFilter(e.target.value)}
    className="sm:w-48"
    aria-label="Filter by authority"
>
    <option value="">All authorities</option>
    <option value="TFL">TFL</option>
    <option value="Castle Point">Castle Point</option>
    <option value="Other">Other</option>
</Select>
                        
                        <Select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="sm:w-40"
                            aria-label="Filter by status"
                        >
                            <option value="">All statuses</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </Select>
                    </div>
                </div>
            </div>

            {isLoading && <LoadingPage />}
            {isError && <ErrorPage message={error?.message} onRetry={refetch} />}

            {!isLoading && !isError && vehicles.length === 0 && (
                <EmptyState
                    icon={Car}
                    title="No vehicles found"
                    description={search || classFilter !== 'all' || statusFilter !== 'active'
                        ? 'Try adjusting your search or filters.'
                        : 'Add your first vehicle to get started.'}
                    action={isAdmin && (
                        <Button onClick={() => navigate('/vehicles/new')}>
                            <Plus className="h-4 w-4" />
                            Add vehicle
                        </Button>
                    )}
                />
            )}

            {!isLoading && !isError && vehicles.length > 0 && (
                <>
                    {/* Desktop table */}
                    <div className="hidden md:block card overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-paper-hover border-b border-paper-border text-xs font-medium text-ink-subtle uppercase tracking-wider">
                                <tr>
                                    <th className="text-left px-6 py-3 w-32">Registration</th>
                                    <th className="text-left px-6 py-3">Vehicle</th>
                                    <th className="text-left px-6 py-3 w-28">Class</th>
                                    <th className="text-left px-6 py-3">Authority</th>
                                    <th className="text-left px-6 py-3 w-72">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vehicles.map(v => (
                                    <tr
                                        key={v.id}
                                        onClick={() => navigate(`/vehicles/${v.id}`)}
                                        className="border-b border-paper-border-soft last:border-0 cursor-pointer hover:bg-paper-hover/50 transition-colors"
                                    >
                                        <td className="px-6 py-4">
                                            <RegistrationBadge registration={v.registration} />
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="font-medium text-ink">
                                                {v.make} {v.model}
                                                {v.year && <span className="text-ink-subtle font-normal ml-1.5">({v.year})</span>}
                                            </div>
{v.nickname && (
                                                <div className="text-xs text-ink-subtle mt-0.5">"{v.nickname}"</div>
                                            )}
                                            {v.fleet_partner_id && (
                                                <div className="mt-1">
                                                    <PartnerBadge name={v.fleet_partner_company_name} />
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <ClassBadge vehicleClass={v.vehicle_class} />
                                        </td>
                                        
                                        <td className="px-6 py-4">
                                            <span className="text-ink text-xs">{v.licensing_authority}</span>
                                        </td>
                                        <td className="px-6 py-4">
    <StatusWithReasons entity={v} />
</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile cards */}
                    <div className="md:hidden space-y-3">
                        {vehicles.map(v => (
                            <div
                                key={v.id}
                                onClick={() => navigate(`/vehicles/${v.id}`)}
                                className="card card-body cursor-pointer hover:shadow-card-hover transition-shadow"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-3 min-w-0 flex-1">
                                        <RegistrationBadge registration={v.registration} />
                                        <div className="min-w-0 flex-1">
                                            <div className="font-medium text-ink">
                                                {v.make} {v.model}
                                                {v.year && <span className="text-ink-subtle font-normal ml-1.5 text-xs">({v.year})</span>}
                                            </div>
                                            {v.nickname && (
                                                <div className="text-xs text-ink-subtle mt-0.5 truncate">"{v.nickname}"</div>
                                            )}
                                            {v.fleet_partner_id && (
                                                <div className="mt-1">
                                                    <PartnerBadge name={v.fleet_partner_company_name} />
                                                </div>
                                            )}
                                            <div className="flex items-center gap-2 mt-1.5">
                                                <ClassBadge vehicleClass={v.vehicle_class} />
                                                <span className="inline-flex items-center gap-1 text-xs text-ink-muted">
                                                    <PassengersIcon className="h-3 w-3" />
                                                    {v.seats}
                                                </span>
                                                {v.luggage_capacity != null && (
                                                    <span className="inline-flex items-center gap-1 text-xs text-ink-muted">
                                                        <Briefcase className="h-3 w-3" />
                                                        {v.luggage_capacity}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                  <StatusWithReasons entity={v} />
                                </div>
                                <div className="mt-3 pt-3 border-t border-paper-border-soft flex items-center justify-between text-xs">
                                    <span className="text-ink-muted">{v.licensing_authority}</span>
                                    {v.include_in_compliance === false && (
                                        <StatusPill status="neutral">Compliance opt-out</StatusPill>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    <p className="mt-4 text-xs text-ink-subtle">
                        {vehicles.length} {vehicles.length === 1 ? 'vehicle' : 'vehicles'}
                    </p>
                </>
            )}
        </div>
    );
}

/**
 * Monospace UK-style registration badge.
 */
function RegistrationBadge({ registration }) {
    if (!registration) return <span className="text-ink-faint text-xs">—</span>;
    return (
        <span className="inline-flex items-center px-2 py-0.5 rounded bg-ink text-paper font-mono text-xs font-medium tracking-wide whitespace-nowrap">
            {registration}
        </span>
    );
}

function PartnerBadge({ name }) {
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
            <Briefcase className="h-3 w-3" />
                        {name || 'Operator'}
        </span>
    );
}

function ClassBadge({ vehicleClass }) {
    const label = CLASS_LABELS[vehicleClass] || vehicleClass;
    return (
        <span className={cn(
            'inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium',
            CLASS_STYLES[vehicleClass] || 'bg-paper-hover text-ink-muted'
        )}>
            {label}
        </span>
    );
}

const CLASS_LABELS = {
    business: 'Business',
    first: 'First',
    suv: 'SUV',
    xl: 'XL',
};

const CLASS_STYLES = {
    business: 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200',
    first:    'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200',
    suv:      'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200',
    xl:       'bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200',
};

function statusVariant(status) {
    switch (status) {
        case 'active': return 'success';
        case 'inactive': return 'neutral';
        default: return 'neutral';
    }
}

                        function StatusWithReasons({ entity }) {
    const { tone, label, reasons } = computeDisplayStatus(entity);

    if (reasons.length === 0) {
        return <StatusPill status={tone}>{label}</StatusPill>;
    }

    return (
        <div className="flex flex-col items-start gap-1">
            <StatusPill status={tone}>{label}</StatusPill>
            <div className="text-xs text-ink-subtle">
                {reasons.join(' · ')}
            </div>
        </div>
    );
}
